/**
 * Beach Landing — run supplies along the coast in the landing craft.
 *
 * Two beach depots sit on the same shore. Nose onto the sand at the supply
 * dump and drop the ramp: a forklift drives up it and loads four pallets of
 * soda and toilet paper into the cargo well. Raise the ramp, back off, and land
 * at the beach camp down the coast, where a second forklift unloads them. Then
 * the dump restocks for another run.
 *
 * While a forklift is working the boat is held on the beach and the ramp stays
 * down, so nothing can drive out from under it.
 */

import { Group, Vector3, type Object3D } from 'three/webgpu'
import { Beach, bedHeight } from '../world/Beach.ts'
import { Forklift, FORK_REACH, CARRY_HEIGHT } from '../entities/Forklift.ts'
import { loadLandingCargo, type LandingCargo } from '../entities/landingCargoModel.ts'
import type { GameMode, ModeContext } from './GameMode.ts'

const SUPPLY_X = -90
const CAMP_X = 90
/** How far along the shore from a depot's centre the ramp may land. */
const LANDING_HALF_WIDTH = 16
/** The ramp lip must reach water no deeper than this for the forklift. */
const WADING_DEPTH = .7
/** Row of pallets, just above the tide line. */
const DEPOT_Z = 9
/** Forklifts square up this far seaward of a pallet before driving in. */
const APPROACH = 2.7
/** Clear sand beyond the ramp lip where the forklift turns. */
const RAMP_APRON = 3.2
const CAMP_SPOTS = 8
// Cargo well slots in the boat frame (+Z bow), aft row first so later
// pallets never block the way in. The well floor is 0.25 m above the waterline.
const BOAT_SLOTS = [[-.6, -1.5], [.6, -1.5], [-.6, -.1], [.6, -.1]].map(([x, z]) => new Vector3(x, .25, z))
const WELL = { aft: -2.2, halfWidth: 1.3 }
// Where the bow first touches the sand: the forefoot is 0.68 m shallower than
// the keel amidships, 4.9 m forward.
const BOW_REACH = 4.9
const FOREFOOT_RISE = .68
/** Light sideways grip on the sand: holds the boat, but full astern backs it off. */
const GROUND_FRICTION = 20_000
/** How hard the sand stops a bow driving up it, per second at full grip. */
const BEACHING_BITE = 14

type Phase = 'to-supply' | 'loading' | 'to-camp' | 'unloading'

interface Depot {
  x: number
  forklift: Forklift | null
  home: Vector3
}

const _tip = new Vector3()
const _local = new Vector3()
const _bow = new Vector3()

export class LandingMode implements GameMode {
  readonly worldGroup: Group
  readonly tugSpawn: { position: Vector3; heading: number }
  readonly ready: Promise<void>

  private readonly beach = new Beach({ boom: false })
  private cargo: LandingCargo | null = null
  private disposed = false
  private phase: Phase = 'to-supply'
  private deliveries = 0
  private readonly supply: Depot = { x: SUPPLY_X, forklift: null, home: new Vector3(SUPPLY_X + 5.5, 0, 6) }
  private readonly camp: Depot = { x: CAMP_X, forklift: null, home: new Vector3(CAMP_X + 8, 0, 6) }
  private readonly supplyPallets: (Object3D | null)[] = [null, null, null, null]
  private readonly campPallets: (Object3D | null)[] = Array(CAMP_SPOTS).fill(null)
  private readonly boatPallets: (Object3D | null)[] = [null, null, null, null]
  private nextCampSpot = 0
  private moved = 0
  private pin: { x: number; z: number; yaw: number } | null = null

  constructor(private readonly ctx: ModeContext) {
    this.worldGroup = this.beach.group
    this.worldGroup.name = 'landing-beach'
    this.tugSpawn = { position: new Vector3(0, 0, -120), heading: 0 }
    this.ready = loadLandingCargo().then(cargo => {
      if (this.disposed) { cargo.dispose(); return }
      this.cargo = cargo
      this.buildDepots(cargo)
    })
  }

  private buildDepots(cargo: LandingCargo): void {
    const surface = (x: number, z: number) => this.surface(x, z)
    for (const depot of [this.supply, this.camp]) {
      const truck = cargo.clone('forklift')
      this.worldGroup.add(truck)
      depot.forklift = new Forklift(truck, surface)
      depot.forklift.place(depot.home.x, depot.home.z, Math.PI)
    }
    const prop = (piece: 'sign_supply' | 'sign_camp' | 'camp_tent', x: number, z: number, heading: number): void => {
      const object = cargo.clone(piece)
      object.position.set(x, bedHeight(x, z) - .05, z)
      object.rotation.y = heading
      this.worldGroup.add(object)
    }
    prop('sign_supply', SUPPLY_X - 6.5, 8, 0)
    prop('sign_camp', CAMP_X - 9.5, 8, 0)
    prop('camp_tent', CAMP_X - 4, 17, Math.PI / 2)
    prop('camp_tent', CAMP_X + 4, 17, Math.PI / 2)
    this.restock()
  }

  private supplySpot(index: number): Vector3 {
    const x = SUPPLY_X - 2.4 + index * 1.6
    return new Vector3(x, bedHeight(x, DEPOT_Z), DEPOT_Z)
  }

  private campSpot(index: number): Vector3 {
    const x = CAMP_X - 5.6 + index * 1.6
    return new Vector3(x, bedHeight(x, DEPOT_Z), DEPOT_Z)
  }

  private restock(): void {
    for (let i = 0; i < this.supplyPallets.length; i++) {
      if (this.supplyPallets[i]) continue
      const pallet = this.cargo!.clone(i % 2 === 0 ? 'pallet_soda' : 'pallet_paper')
      pallet.position.copy(this.supplySpot(i))
      this.worldGroup.add(pallet)
      this.supplyPallets[i] = pallet
    }
  }

  start(): void {
    const { tug, hud } = this.ctx
    // Ground at the bow: a landing craft runs its forefoot onto the sand and
    // stays there until it backs off.
    tug.bedSampler = (x, z) => Math.max(
      bedHeight(x, z),
      bedHeight(x + Math.sin(tug.yaw) * BOW_REACH, z + Math.cos(tug.yaw) * BOW_REACH) - FOREFOOT_RISE,
    )
    tug.config.groundFriction = GROUND_FRICTION * (tug.config.mass / 60_000)
    tug.ramp?.reset()
    hud.setEarningsHidden(true)
    hud.setHint('W/S throttle · A/D helm · R midships · C camera · V weather')
    hud.announce(tug.ramp ? 'Land at the supply dump and drop the ramp' : 'Beach landings need the landing craft', 6)
  }

  groundHeight(x: number, z: number): number {
    return bedHeight(x, z)
  }

  rampLocked(): string | null {
    return this.phase === 'loading' || this.phase === 'unloading' ? 'Hold on — the forklift is still working!' : null
  }

  /** What the forklift's wheels are on: cargo deck, ramp, or sand. */
  private surface(x: number, z: number): { y: number; careful: boolean } {
    const { tug } = this.ctx
    const ramp = tug.ramp
    if (ramp) {
      _local.set(x - tug.position.x, .25, z - tug.position.z)
      _local.applyAxisAngle(_bow.set(0, 1, 0), -tug.yaw)
      if (Math.abs(_local.x) < WELL.halfWidth) {
        const tip = ramp.tipLocal(_tip)
        let deckY: number | null = null
        if (_local.z >= WELL.aft && _local.z <= ramp.hinge.z) deckY = ramp.hinge.y
        else if (ramp.angle > 1 && _local.z > ramp.hinge.z && _local.z <= tip.z) {
          deckY = ramp.hinge.y + (tip.y - ramp.hinge.y) * (_local.z - ramp.hinge.z) / (tip.z - ramp.hinge.z)
        }
        if (deckY !== null) {
          _local.y = deckY
          return { y: tug.localToWorld(_local, _local).y, careful: true }
        }
      }
    }
    return { y: bedHeight(x, z), careful: false }
  }

  private landedAt(depot: Depot): boolean {
    const { tug } = this.ctx
    if (!tug.ramp?.down || tug.speed > .8) return false
    tug.ramp.tipWorld(tug, _tip)
    return Math.abs(_tip.x - depot.x) < LANDING_HALF_WIDTH && bedHeight(_tip.x, _tip.z) > -WADING_DEPTH
  }

  /** Nosed onto the depot's beach but the ramp is still up. */
  private beachedAt(depot: Depot): boolean {
    const { tug } = this.ctx
    tug.localToWorld(_bow.set(0, 0, BOW_REACH), _bow)
    return Math.abs(_bow.x - depot.x) < LANDING_HALF_WIDTH && bedHeight(_bow.x, _bow.z) > -1.4
  }

  fixedStep(dt: number): void {
    const { tug } = this.ctx
    // The flat bow ploughs into the sand going forward, but slides back off it.
    const ahead = tug.forwardSpeed
    if (tug.grounding > 0 && ahead > 0) {
      const lost = ahead * (tug.grounding > .7 ? 1 : 1 - Math.exp(-BEACHING_BITE * tug.grounding * dt))
      tug.velocity.x -= Math.sin(tug.yaw) * lost
      tug.velocity.z -= Math.cos(tug.yaw) * lost
    }
    if (this.pin) {
      // Hold the boat on the sand under the forklift; it still rides the swell.
      tug.position.x = this.pin.x
      tug.position.z = this.pin.z
      tug.yaw = this.pin.yaw
      tug.velocity.x = 0
      tug.velocity.z = 0
      tug.yawRate = 0
    }
    if (!this.cargo || !tug.ramp) return
    this.supply.forklift!.step(dt)
    this.camp.forklift!.step(dt)

    if (this.phase === 'to-supply' && this.landedAt(this.supply)) this.beginLoading()
    else if (this.phase === 'to-camp' && this.landedAt(this.camp)) this.beginUnloading()
  }

  private hold(): void {
    const { tug } = this.ctx
    this.pin = { x: tug.position.x, z: tug.position.z, yaw: tug.yaw }
    this.moved = 0
  }

  /** Ramp lip, and the turning apron of sand just beyond it, in world space. */
  private rampRoute(): { tip: Vector3; apron: Vector3 } {
    const { tug } = this.ctx
    const tipLocal = tug.ramp!.tipLocal(new Vector3())
    const apron = tug.localToWorld(new Vector3(0, 0, tipLocal.z + RAMP_APRON), new Vector3())
    return { tip: tipLocal.setY(0), apron }
  }

  /** Up the ramp and forks-first into a cargo slot, from the apron. */
  private intoBoat(forklift: Forklift, slot: Vector3, tip: Vector3): void {
    const boat = this.ctx.tug
    const hinge = boat.ramp!.hinge
    forklift
      .drive(new Vector3(0, 0, tip.z - .05), { boat })
      .drive(new Vector3(0, 0, hinge.z - .3), { boat })
      .drive(new Vector3(slot.x, 0, slot.z + FORK_REACH + 1.3), { boat })
      .drive(new Vector3(slot.x, 0, slot.z + FORK_REACH), { boat })
  }

  /** Reverse back down the ramp to the apron. */
  private outOfBoat(forklift: Forklift, slot: Vector3, tip: Vector3, apron: Vector3): void {
    const boat = this.ctx.tug
    const hinge = boat.ramp!.hinge
    forklift
      .drive(new Vector3(slot.x, 0, slot.z + FORK_REACH + 1.3), { boat, reverse: true })
      .drive(new Vector3(0, 0, hinge.z - .3), { boat, reverse: true })
      .drive(new Vector3(0, 0, tip.z - .05), { boat, reverse: true })
      .drive(apron, { reverse: true })
  }

  private pick(forklift: Forklift, take: () => Object3D): void {
    forklift.then(() => {
      const pallet = take()
      this.worldGroup.add(pallet)
      forklift.load = pallet
    }).forks(CARRY_HEIGHT)
  }

  private beginLoading(): void {
    this.phase = 'loading'
    this.hold()
    const forklift = this.supply.forklift!
    const { tip, apron } = this.rampRoute()
    this.ctx.hud.announce('Ramp down — here comes the forklift!', 3)
    BOAT_SLOTS.forEach((slot, index) => {
      const spot = this.supplySpot(index)
      const approach = new Vector3(spot.x, 0, spot.z - APPROACH)
      forklift.drive(approach).face(0).drive(new Vector3(spot.x, 0, spot.z - FORK_REACH))
      this.pick(forklift, () => {
        const pallet = this.supplyPallets[index]!
        this.supplyPallets[index] = null
        return pallet
      })
      forklift.drive(approach, { reverse: true }).drive(apron)
      this.intoBoat(forklift, slot, tip)
      forklift.forks(0).then(() => this.stow(forklift, index))
      this.outOfBoat(forklift, slot, tip, apron)
    })
    forklift.drive(this.supply.home).face(Math.PI).then(() => {
      this.phase = 'to-camp'
      this.pin = null
      this.ctx.hud.announce('All loaded! Raise the ramp and take it to the beach camp', 5)
    })
  }

  private beginUnloading(): void {
    this.phase = 'unloading'
    this.hold()
    const forklift = this.camp.forklift!
    const { tip, apron } = this.rampRoute()
    this.ctx.hud.announce('Ramp down — unloading!', 3)
    // Front row first, so the forklift never reaches past a pallet.
    for (let index = BOAT_SLOTS.length - 1; index >= 0; index--) {
      const slot = BOAT_SLOTS[index]
      const spotIndex = this.nextCampSpot
      this.nextCampSpot = (this.nextCampSpot + 1) % CAMP_SPOTS
      // The camp eats its oldest supplies to make room.
      const old = this.campPallets[spotIndex]
      if (old) this.worldGroup.remove(old)
      this.campPallets[spotIndex] = null
      const spot = this.campSpot(spotIndex)
      const approach = new Vector3(spot.x, 0, spot.z - APPROACH)
      forklift.drive(apron)
      this.intoBoat(forklift, slot, tip)
      this.pick(forklift, () => {
        const pallet = this.boatPallets[index]!
        this.boatPallets[index] = null
        return pallet
      })
      this.outOfBoat(forklift, slot, tip, apron)
      forklift.drive(approach).face(0).drive(new Vector3(spot.x, 0, spot.z - FORK_REACH)).forks(0).then(() => {
        const pallet = forklift.load!
        forklift.load = null
        pallet.position.copy(spot)
        pallet.rotation.set(0, 0, 0)
        this.campPallets[spotIndex] = pallet
        this.moved++
      })
      forklift.drive(approach, { reverse: true })
    }
    forklift.drive(this.camp.home).face(Math.PI).then(() => {
      this.deliveries++
      this.phase = 'to-supply'
      this.pin = null
      this.restock()
      this.ctx.hud.announce('Delivered! The camp says thanks. More supplies wait at the dump', 5)
    })
  }

  /** Set the carried pallet down in the cargo well; it rides with the boat now. */
  private stow(forklift: Forklift, index: number): void {
    const pallet = forklift.load!
    forklift.load = null
    this.ctx.tug.group.add(pallet)
    pallet.position.copy(BOAT_SLOTS[index])
    pallet.rotation.set(0, 0, 0)
    this.boatPallets[index] = pallet
    this.moved++
  }

  toggleHook(): void {}

  frame(): void {
    const { tug, hud, waypoint, chase } = this.ctx
    hud.setHookState(false, false)
    const depot = this.phase === 'to-supply' ? this.supply : this.phase === 'to-camp' ? this.camp : null
    const target = depot ? new Vector3(depot.x, 0, -6) : null
    const distance = waypoint.update(tug.position, target, chase.camera)
    const subline = `${this.deliveries} ${this.deliveries === 1 ? 'delivery' : 'deliveries'}`
    if (!tug.ramp) {
      hud.setMission('Pick the landing craft from the menu', subline, 0)
      return
    }
    const away = distance >= 30 ? ` · ${distance >= 950 ? `${(distance / 1000).toFixed(1)} km` : `${Math.round(distance / 10) * 10} m`} away` : ''
    let text: string
    if (this.phase === 'loading') text = `Forklift loading… ${this.moved} of ${BOAT_SLOTS.length} pallets aboard`
    else if (this.phase === 'unloading') text = `Unloading at the camp… ${this.moved} of ${BOAT_SLOTS.length} ashore`
    else if (depot && this.beachedAt(depot) && !tug.ramp.open) text = 'Beached! Lower the ramp'
    else if (depot && tug.ramp.open && !tug.ramp.down) text = 'Ramp going down…'
    else if (this.phase === 'to-camp' && tug.ramp.open) text = 'Raise the ramp and back off the beach'
    else if (this.phase === 'to-supply') text = 'Land at the supply dump' + away
    else text = 'Take the supplies to the beach camp' + away
    hud.setMission(text, subline, 0)
  }

  towWake(): null {
    return null
  }

  dispose(): void {
    this.disposed = true
    for (const pallet of this.boatPallets) pallet?.removeFromParent()
    this.ctx.scene.remove(this.worldGroup)
    this.worldGroup.clear()
    this.cargo?.dispose()
    this.ctx.tug.bedSampler = null
    this.ctx.tug.config.groundFriction = 0
    this.ctx.tug.ramp?.reset()
  }
}
